import React, { useMemo } from "react";
import { View, Text, StyleSheet, ActivityIndicator } from "react-native";
import { useSelector } from "react-redux";
import LhButton from "../../../components/LhButton";
import lightStyles, { createHomeScreenThemeStyles } from "../HomeScreen.style";
import { FontFamily } from "../../../constants/fonts";
import { CIDashboardData } from "../types";
import { TEXT } from "../../../constants/texts";
import { COLORS } from "../../../constants/themes";
import { useTheme } from "../../../theme";

interface CIActiveItemsProps {
  setSelectedCiStatus: (status: "On Track" | "Off Track" | "All") => void;
  setCiOverlayVisible: (isVisible: boolean) => void;
}

// Content state component moved outside to avoid creating components during render
interface ContentStateProps {
  ciData: CIDashboardData;
  setSelectedCiStatus: (status: "On Track" | "Off Track" | "All") => void;
  setCiOverlayVisible: (isVisible: boolean) => void;
}

const ContentState: React.FC<ContentStateProps> = ({
  ciData,
  setSelectedCiStatus,
  setCiOverlayVisible,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const themeStyles = useMemo(() => createHomeScreenThemeStyles(colors), [colors]);
  const isDark = resolvedTheme === "dark";
  // Use API-provided percentages or fallback to calculated values
  const activePercent = ciData?.activePercentage ?? 
    (ciData?.counts?.total ? Math.round((ciData.counts.onTrack / ciData.counts.total) * 100) : 0);
  
  const onTrackPercent = ciData?.onTrackPercentage ?? 
    (ciData?.counts?.total ? Math.round((ciData.counts.onTrack / ciData.counts.total) * 100) : 0);

  return (
    <View style={lightStyles.ciActiveButtonsContainer}>
      {/* Button 1: Active CI Items (light blue) */}
      <LhButton
        testID="ci-total"
        text=""
        variant="secondary"
        size="medium"
        onPress={() => {
          setSelectedCiStatus("All");
          setCiOverlayVisible(true);
        }}
        isLoading={false}
        disabled={false}
        style={StyleSheet.flatten([
          lightStyles.ciActiveButton,
          {
            backgroundColor: isDark ? colors.brand.primary : "#82CDF8",
            marginBottom: 12,
          },
        ])}
      >
        <View style={lightStyles.ciActiveButtonRow}>
          <Text style={[lightStyles.ciActiveButtonLabel, isDark ? themeStyles.inverseText : { color: "#3A3A3A" }]}>Active CI Items:&nbsp;</Text>
          <Text style={[lightStyles.ciActiveButtonValue, isDark ? themeStyles.inverseText : { color: "#3A3A3A" }]}> 
            {ciData.counts.total}
          </Text>
        </View>
      </LhButton>

      {/* Button 2: Active Percentage (green) */}
      <LhButton
        testID="ci-active-percent"
        text=""
        variant="secondary"
        size="medium"
        onPress={() => {}}
        isLoading={false}
        disabled={false}
        style={StyleSheet.flatten([
          lightStyles.ciActiveButton,
          {
            backgroundColor: isDark ? colors.status.success : "#027C06",
            marginBottom: 12,
          },
        ])}
      >
        <View style={lightStyles.ciActiveButtonRow}>
          <Text style={[lightStyles.ciActiveButtonLabel, themeStyles.inverseText]}>Active:&nbsp;</Text>
          <Text style={[lightStyles.ciActiveButtonValue, themeStyles.inverseText]}>{activePercent}%</Text>
        </View>
      </LhButton>

      {/* Button 3: On Track (green) - keep existing behavior */}
      <LhButton
        testID="ci-on-track"
        text=""
        variant="primary"
        size="medium"
        onPress={() => {
          setSelectedCiStatus("On Track");
          setCiOverlayVisible(true);
        }}
        isLoading={false}
        disabled={false}
        style={StyleSheet.flatten([
          lightStyles.ciActiveButton,
          {
            backgroundColor: isDark ? colors.status.success : "#027C06",
            marginBottom: 12,
          },
        ])}
      >
        <View style={lightStyles.ciActiveButtonRow}>
          <Text style={[lightStyles.ciActiveButtonLabel, themeStyles.inverseText]}>On Track:&nbsp;</Text>
          <Text style={[lightStyles.ciActiveButtonValue, themeStyles.inverseText]}> {onTrackPercent}%</Text>
        </View>
      </LhButton>

      {/* Button 4: Off Track (pink) - keep existing behavior */}
      <LhButton
        testID="ci-off-track"
        text=""
        variant="danger"
        size="medium"
        onPress={() => {
          setSelectedCiStatus('Off Track');
          setCiOverlayVisible(true);
        }}
        isLoading={false}
        disabled={false}
        style={StyleSheet.flatten([
          lightStyles.ciActiveButton,
          { backgroundColor: isDark ? colors.status.error : '#AB2B09' },
        ])}
      >
        <View style={lightStyles.ciActiveButtonRow}>
          <Text style={[lightStyles.ciActiveButtonLabel, themeStyles.inverseText]}>Off Track - 30 days:&nbsp;</Text>
          <Text style={[lightStyles.ciActiveButtonValue, themeStyles.inverseText]}> 
            {ciData.counts.offTrack30days}
          </Text>
        </View>
      </LhButton>
    </View>
  );
};

const CIActiveItems: React.FC<CIActiveItemsProps> = ({
  setSelectedCiStatus,
  setCiOverlayVisible,
}) => {
  const { colors } = useTheme();
  const themeStyles = useMemo(() => createHomeScreenThemeStyles(colors), [colors]);
  const { ciDashboardData, loading } = useSelector(
    (state: any) => state.homescreen
  );
  const { defaultCiData } = require('../staticData');

  // Use ciDashboardData from Redux store instead of static data
  const ciData: CIDashboardData = ciDashboardData || defaultCiData;

  return (
    <View style={[lightStyles.topRightCard, themeStyles.cardSurface, lightStyles.ciActiveContainer]}>
      <Text style={[lightStyles.ciActiveHeader, themeStyles.ciActiveHeader]} testID="ci-header-text">
        {TEXT.HEADERS.CI_ACTIVE_ITEMS}
      </Text>

      <View style={lightStyles.ciActiveContentContainer}>
        {loading?.ciDashboard ? (
          <View style={lightStyles.ciActiveLoadingContainer}>
            <ActivityIndicator
              size="large"
              color={colors.brand.primary}
              testID="ci-loading-indicator"
            />
            <Text style={[lightStyles.ciActiveLoadingText, themeStyles.ciActiveLoadingText]}>
              {TEXT.CI_DASHBOARD.LOADING}
            </Text>
          </View>
        ) : (
          <ContentState
            ciData={ciData}
            setSelectedCiStatus={setSelectedCiStatus}
            setCiOverlayVisible={setCiOverlayVisible}
          />
        )}
      </View>
    </View>
  );
};

export default CIActiveItems;
